import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { EMPTY, Subject } from 'rxjs';
import { catchError, concatMap, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../services/auth.service';
import { TranslocoService } from '@ngneat/transloco';
import { DocumentService } from "../../services/document.service";
import { ReCaptchaV3Service } from "ng-recaptcha";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  destroy$ = new Subject();
  @ViewChild('loginErrorTpl', {static: true}) loginErrorTpl!: TemplateRef<{}>;
  @ViewChild('socialLoginErrorTpl', {static: true}) socialLoginErrorTpl!: TemplateRef<{}>;

  loginForm!: FormGroup;
  socialLoginError = '';
  loading = false;
  logo = ''
  isInputPhoneNumber = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NzNotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private translocoService: TranslocoService,
    private documentService: DocumentService,
    private recaptchaV3Service: ReCaptchaV3Service,
  ) {
    if (this.authService.accessToken) {
      this.router.navigateByUrl('/');
      return;
    }
  }

  ngOnInit(): void {
    const email = this.route.snapshot.queryParams.email;
    this.loginForm = this.fb.group({
      email: [email || '', [Validators.required]],
      password: ['', [Validators.required]],
      remember: [true]
    });
    this.trackSocialLogin();
    this.getLogo();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  trackSocialLogin() {
    this.authService.trackSocialUser()
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
        },
        error: (err) => {
          this.authService.signOutSocial();
          this.trackSocialLogin();
          this.socialLoginError = this.translocoService.translate('login.socialLoginError');
          if (err.status === 400) {
            this.socialLoginError = this.translocoService.translate('login.existGmail');
            if (err.error.error.messageKey === 'social_auth_login_require_email') {
              this.socialLoginError = this.translocoService.translate('login.socialRequireEmail');
            }
          }
          this.notificationService.template(
            this.socialLoginErrorTpl,
            {
              nzCloseIcon: '',
              nzStyle: {backgroundColor: 'transparent', padding: 0}
            }
          );
        }
      });
  }

  signInWithFb(): void {
    this.authService.signInWithFb();
  }

  signInWithGg(): void {
    this.authService.signInWithGg();
  }

  login(): void {
    if (this.loginForm.valid) {
      this.loading = true;
      let req = {};
      if (this.isInputPhoneNumber) {
        req = {
          phone: this.loginForm.get('email')?.value,
          password: this.loginForm.get('password')?.value
        }
      } else {
        req = {
          email: this.loginForm.get('email')?.value,
          password: this.loginForm.get('password')?.value
        }
      }

      this.recaptchaV3Service.execute('login').pipe(
        catchError(() => {
          this.notificationService.error(
            '',
            this.translocoService.translate('common.failed'),
          );
          return EMPTY;
        }),
        concatMap((token) => {
          this.authService.setValueRecapcha(token);
          return this.authService.login(req).pipe(
            catchError((err) => {
              this.loading = false;
              if (err.status === 400) {
                this.notificationService.template(
                  this.loginErrorTpl,
                  {
                    nzCloseIcon: '',
                    nzStyle: {backgroundColor: 'transparent', padding: 0}
                  }
                );
              }
              return EMPTY;
            })
          );
        })
      ).subscribe(() => {
        this.loading = false;
      });
    } else {
      Object.values(this.loginForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({onlySelf: true});
        }
      });
    }
  }

  getLogo() {
    this.logo = this.documentService.getLogoPage();
    if (!this.logo) {
      this.documentService.getLogo().pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.logo = res.logo;
          this.documentService.setLogoPage(this.logo);
        }
      })
    }
  }

  onChangeEmailOrPhone(emailOrPhone: string) {
    if (!emailOrPhone) {
      return;
    }

    this.isInputPhoneNumber = /(0[3|5|7|8|9])+([0-9]{8})\b/.test(emailOrPhone) && !emailOrPhone.includes('@');
  }

}
